// Food Pack 2 (M19): 32 more food filters in the Mega Food Pack's four sections, added 2026-10-11 at the
// owner's request. Same format and rules as foodPack.ts: recipes of existing settings only, "add" moves a
// value, "set" goes to a value, Strength scales every change; never hsl.green.h or hsl.yellow.h; inside the
// clamp ranges in simpleFilters.ts. Starting values for the owner to tune on real menu photos.
// Three names in the owner's list already existed (Ice Cream, Pastel Brunch, Bright & Airy); those are
// shipped under a distinct name and look (PROGRESS.md, M19).
import type { Pt } from '../model/types';
import type { SimpleFilter } from './simpleFilters';
import type { FoodSection } from './foodPack';

type Op = SimpleFilter['ops'][number];
type PackFilter = SimpleFilter & { section: FoodSection };

const lift = (black: number, white = 1): Pt[] => [
  { x: 0, y: black },
  { x: 1, y: white },
];
const p = (
  id: string,
  section: FoodSection,
  name: string,
  nameEs: string,
  ops: Op[],
  curve?: Pt[],
): PackFilter => ({
  id: `fp2-${id}`,
  group: 'food',
  section,
  name,
  nameEs,
  ops,
  ...(curve ? { curve } : {}),
});

export const FOOD_PACK_2: PackFilter[] = [
  // ── Fix the light ────────────────────────────────────────────────────────────
  p('warm-bulbs', 'fix', 'Warm Bulbs', 'Focos cálidos', [
    ['wb.temp', 'add', -35],
    ['wb.tint', 'add', 6],
    ['light.exposure', 'add', 0.15],
    ['light.highlights', 'add', -15],
    ['light.shadows', 'add', 15],
    ['hsl.orange.s', 'add', -10],
    ['color.vibrance', 'add', 8],
  ]),
  p('mixed-light', 'fix', 'Mixed Light (window + bulbs)', 'Luz mixta (ventana + focos)', [
    ['wb.temp', 'add', -10],
    ['wb.tint', 'add', 3],
    ['light.highlights', 'add', -25],
    ['light.shadows', 'add', 20],
    ['hsl.orange.s', 'add', -8],
    ['hsl.blue.s', 'add', -15],
    ['hsl.aqua.s', 'add', -10],
    ['effects.splitTone.shadowHue', 'set', 40],
    ['effects.splitTone.shadowSat', 'add', 12],
    ['effects.splitTone.highlightHue', 'set', 210],
    ['effects.splitTone.highlightSat', 'add', 10],
  ]),
  p('phone-night', 'fix', 'Phone Night Mode', 'Modo noche del teléfono', [
    ['light.exposure', 'add', -0.15],
    ['light.contrast', 'add', 18],
    ['light.highlights', 'add', -20],
    ['light.shadows', 'add', -15],
    ['light.blacks', 'add', -15],
    ['wb.temp', 'add', 6],
    ['color.vibrance', 'add', 10],
    ['detail.nrLuma', 'add', 15],
    ['detail.nrColor', 'add', 30],
  ]),
  p(
    'sauce-glare',
    'fix',
    'Sauce Glare',
    'Brillo de salsa',
    [
      ['light.highlights', 'add', -60],
      ['light.whites', 'add', -20],
      ['light.contrast', 'add', 10],
      ['color.saturation', 'add', 8],
      ['hsl.red.s', 'add', 8],
      ['hsl.orange.l', 'add', -4],
    ],
    lift(0, 0.92),
  ),
  p('blue-shade', 'fix', 'Blue Shade', 'Sombra azul', [
    ['wb.temp', 'add', 25],
    ['wb.tint', 'add', 4],
    ['light.exposure', 'add', 0.25],
    ['light.shadows', 'add', 15],
    ['color.vibrance', 'add', 12],
    ['hsl.blue.s', 'add', -20],
    ['hsl.aqua.s', 'add', -15],
  ]),
  p(
    'steamy-lens',
    'fix',
    'Steamy Lens',
    'Lente empañado',
    [
      ['light.contrast', 'add', 25],
      ['light.blacks', 'add', -20],
      ['light.highlights', 'add', -20],
      ['light.whites', 'add', -10],
      ['color.saturation', 'add', 8],
      ['color.vibrance', 'add', 10],
      ['detail.sharpAmount', 'add', 50],
    ],
    [
      { x: 0, y: 0 },
      { x: 0.3, y: 0.24 },
      { x: 1, y: 1 },
    ],
  ),

  // ── By dish ──────────────────────────────────────────────────────────────────
  p(
    'pasta',
    'dish',
    'Pasta',
    'Pasta',
    [
      ['wb.temp', 'add', 10],
      ['light.contrast', 'add', 10],
      ['light.highlights', 'add', -30],
      ['light.shadows', 'add', 15],
      ['color.saturation', 'add', -5],
      ['color.vibrance', 'add', 12],
      ['hsl.red.s', 'add', 18],
      ['hsl.orange.s', 'add', 6],
      ['hsl.yellow.l', 'add', 4],
      ['effects.splitTone.highlightHue', 'set', 45],
      ['effects.splitTone.highlightSat', 'add', 10],
      ['effects.vignette.amount', 'add', -15],
      ['detail.sharpAmount', 'add', 40],
    ],
    lift(0.03),
  ),
  p('soups-caldos', 'dish', 'Soups & Caldos', 'Sopas y caldos', [
    ['wb.temp', 'add', 10],
    ['light.contrast', 'add', 8],
    ['light.highlights', 'add', -30],
    ['light.shadows', 'add', 15],
    ['color.vibrance', 'add', 8],
    ['hsl.red.s', 'add', 12],
    ['hsl.red.l', 'add', -4],
    ['hsl.orange.s', 'add', 8],
  ]),
  p(
    'tamales',
    'dish',
    'Tamales & Chuchitos',
    'Tamales y chuchitos',
    [
      ['wb.temp', 'add', 10],
      ['light.exposure', 'add', -0.1],
      ['light.contrast', 'add', 10],
      ['light.shadows', 'add', 12],
      ['hsl.green.s', 'add', 15],
      ['hsl.green.l', 'add', -10],
      ['hsl.yellow.s', 'add', 10],
      ['hsl.yellow.l', 'add', 6],
      ['hsl.red.s', 'add', 10],
      ['effects.splitTone.shadowHue', 'set', 100],
      ['effects.splitTone.shadowSat', 'add', 12],
      ['effects.vignette.amount', 'add', -20],
      ['detail.sharpAmount', 'add', 30],
    ],
    lift(0.03),
  ),
  p('rice-beans', 'dish', 'Rice & Beans', 'Arroz y frijoles', [
    ['wb.temp', 'add', -4],
    ['light.contrast', 'add', 25],
    ['light.highlights', 'add', -15],
    ['light.blacks', 'add', -25],
    ['color.saturation', 'add', -5],
    ['cleanWhites.amount', 'add', 50],
    ['detail.sharpAmount', 'add', 40],
  ]),
  p('steak', 'dish', 'Steak', 'Carnes y steak', [
    ['light.contrast', 'add', 20],
    ['light.blacks', 'add', -12],
    ['light.highlights', 'add', -10],
    ['wb.temp', 'add', 6],
    ['hsl.red.s', 'add', 10],
    ['hsl.red.l', 'add', -6],
    ['hsl.orange.l', 'add', -6],
    ['effects.vignette.amount', 'add', -20],
    ['detail.sharpAmount', 'add', 55],
  ]),
  p('ceviche', 'dish', 'Ceviche', 'Ceviche', [
    ['wb.temp', 'add', -15],
    ['light.exposure', 'add', 0.3],
    ['light.contrast', 'add', 12],
    ['light.highlights', 'add', -15],
    ['color.vibrance', 'add', 28],
    ['hsl.green.s', 'add', 20],
    ['hsl.aqua.s', 'add', 10],
    ['hsl.purple.s', 'add', 18],
    ['hsl.red.s', 'add', 8],
    ['cleanWhites.amount', 'add', 25],
    ['detail.sharpAmount', 'add', 40],
  ]),
  p(
    'ice-cream-parlor',
    'dish',
    'Ice Cream Parlor',
    'Heladería',
    [
      ['light.exposure', 'add', 0.4],
      ['light.highlights', 'add', -25],
      ['wb.temp', 'add', -8],
      ['wb.tint', 'add', 6],
      ['color.saturation', 'add', -10],
      ['color.vibrance', 'add', 15],
      ['hsl.magenta.s', 'add', 12],
      ['hsl.purple.s', 'add', 10],
    ],
    lift(0.06),
  ),
  p('shakes', 'dish', 'Shakes', 'Malteadas', [
    ['light.exposure', 'add', 0.45],
    ['light.contrast', 'add', 15],
    ['light.highlights', 'add', -30],
    ['wb.temp', 'add', 10],
    ['wb.tint', 'add', 10],
    ['color.vibrance', 'add', 20],
    ['hsl.red.s', 'add', 18],
    ['hsl.magenta.s', 'add', 18],
    ['cleanWhites.amount', 'add', 35],
    ['effects.splitTone.highlightHue', 'set', 330],
    ['effects.splitTone.highlightSat', 'add', 10],
    ['effects.vignette.amount', 'add', -10],
    ['detail.sharpAmount', 'add', 35],
  ]),
  p('eggs-brunch', 'dish', 'Eggs & Brunch', 'Huevos y brunch', [
    ['light.exposure', 'add', 0.3],
    ['light.contrast', 'add', 15],
    ['light.highlights', 'add', -25],
    ['light.shadows', 'add', 15],
    ['wb.temp', 'add', -4],
    ['hsl.yellow.s', 'add', 15],
    ['hsl.orange.s', 'add', 12],
    ['cleanWhites.amount', 'add', 40],
    ['detail.sharpAmount', 'add', 35],
  ]),
  p('ramen-noodles', 'dish', 'Ramen & Noodles', 'Ramen y fideos', [
    ['light.contrast', 'add', 18],
    ['light.blacks', 'add', -10],
    ['light.highlights', 'add', -30],
    ['wb.temp', 'add', 5],
    ['hsl.orange.s', 'add', 12],
    ['hsl.orange.l', 'add', 5],
    ['effects.vignette.amount', 'add', -25],
    ['detail.sharpAmount', 'add', 45],
  ]),
  p('curry', 'dish', 'Curry', 'Curry', [
    ['wb.temp', 'add', 14],
    ['light.contrast', 'add', 12],
    ['light.shadows', 'add', 10],
    ['color.vibrance', 'add', 10],
    ['hsl.orange.s', 'add', 18],
    ['hsl.orange.l', 'add', -4],
    ['hsl.yellow.s', 'add', 10],
    ['hsl.red.s', 'add', 8],
    ['detail.sharpAmount', 'add', 35],
  ]),
  p('wine', 'dish', 'Wine', 'Vino', [
    ['light.exposure', 'add', -0.2],
    ['light.contrast', 'add', 18],
    ['light.blacks', 'add', -12],
    ['light.highlights', 'add', -20],
    ['wb.temp', 'add', 4],
    ['hsl.red.s', 'add', 15],
    ['hsl.red.l', 'add', -8],
    ['hsl.magenta.s', 'add', 10],
    ['hsl.purple.s', 'add', 10],
    ['effects.vignette.amount', 'add', -30],
    ['effects.splitTone.shadowHue', 'set', 280],
    ['effects.splitTone.shadowSat', 'add', 10],
  ]),
  p(
    'hot-chocolate',
    'dish',
    'Hot Chocolate',
    'Chocolate caliente',
    [
      ['wb.temp', 'add', 16],
      ['wb.tint', 'add', 4],
      ['light.highlights', 'add', -25],
      ['light.shadows', 'add', 15],
      ['hsl.orange.s', 'add', 10],
      ['hsl.orange.l', 'add', -8],
      ['effects.vignette.amount', 'add', -25],
      ['effects.splitTone.highlightHue', 'set', 35],
      ['effects.splitTone.highlightSat', 'add', 12],
      ['effects.grain.amount', 'add', 6],
    ],
    lift(0.02),
  ),
  p('pupusas', 'dish', 'Pupusas', 'Pupusas', [
    ['wb.temp', 'add', 12],
    ['light.contrast', 'add', 18],
    ['light.highlights', 'add', -15],
    ['light.blacks', 'add', -10],
    ['color.vibrance', 'add', 10],
    ['hsl.orange.s', 'add', 12],
    ['hsl.orange.l', 'add', -10],
    ['hsl.green.s', 'add', 10],
    ['hsl.red.s', 'add', 10],
    ['detail.sharpAmount', 'add', 50],
  ]),
  p(
    'pastries-cakes',
    'dish',
    'Pastries & Cakes',
    'Pasteles y repostería',
    [
      ['light.exposure', 'add', 0.4],
      ['light.contrast', 'add', 6],
      ['light.highlights', 'add', -25],
      ['light.shadows', 'add', 15],
      ['wb.temp', 'add', 5],
      ['wb.tint', 'add', 5],
      ['color.saturation', 'add', -6],
      ['hsl.magenta.s', 'add', 15],
      ['hsl.red.s', 'add', 6],
      ['hsl.orange.l', 'add', 4],
      ['cleanWhites.amount', 'add', 25],
      ['effects.splitTone.highlightHue', 'set', 20],
      ['effects.splitTone.highlightSat', 'add', 15],
      ['detail.sharpAmount', 'add', 40],
    ],
    lift(0.04),
  ),

  // ── Styles ───────────────────────────────────────────────────────────────────
  p(
    'bright-airy-plate',
    'style',
    'Bright & Airy Plate',
    'Plato luminoso',
    [
      ['light.exposure', 'add', 0.5],
      ['light.contrast', 'add', -5],
      ['light.highlights', 'add', -35],
      ['light.shadows', 'add', 30],
      ['light.whites', 'add', 10],
      ['wb.temp', 'add', -5],
      ['color.saturation', 'add', -5],
      ['color.vibrance', 'add', 12],
      ['cleanWhites.amount', 'add', 45],
    ],
    lift(0.03),
  ),
  p('black-slate', 'style', 'Black Slate', 'Pizarra negra', [
    ['light.exposure', 'add', -0.35],
    ['light.contrast', 'add', 22],
    ['light.blacks', 'add', -25],
    ['light.shadows', 'add', -10],
    ['light.highlights', 'add', -15],
    ['wb.temp', 'add', -3],
    ['color.vibrance', 'add', 18],
    ['color.saturation', 'add', 5],
    ['effects.vignette.amount', 'add', -30],
    ['detail.sharpAmount', 'add', 50],
  ]),
  p(
    'film-vintage',
    'style',
    'Film Vintage',
    'Película antigua',
    [
      ['wb.temp', 'add', 10],
      ['wb.tint', 'add', 6],
      ['light.contrast', 'add', 10],
      ['color.saturation', 'add', -15],
      ['effects.splitTone.shadowHue', 'set', 180],
      ['effects.splitTone.shadowSat', 'add', 15],
      ['effects.splitTone.highlightHue', 'set', 50],
      ['effects.splitTone.highlightSat', 'add', 18],
      ['effects.grain.amount', 'add', 25],
      ['effects.vignette.amount', 'add', -20],
    ],
    lift(0.07, 0.94),
  ),
  p(
    'peach-pastel',
    'style',
    'Peach Pastel Brunch',
    'Brunch pastel durazno',
    [
      ['light.exposure', 'add', 0.3],
      ['light.contrast', 'add', -15],
      ['wb.temp', 'add', 8],
      ['wb.tint', 'add', 5],
      ['color.saturation', 'add', -12],
      ['hsl.orange.l', 'add', 6],
      ['hsl.red.l', 'add', 6],
      ['hsl.magenta.l', 'add', 8],
      ['effects.splitTone.highlightHue', 'set', 25],
      ['effects.splitTone.highlightSat', 'add', 12],
    ],
    lift(0.07),
  ),
  p('lake-terrace', 'style', 'Lake Terrace', 'Terraza del lago', [
    ['light.exposure', 'add', 0.1],
    ['light.contrast', 'add', 10],
    ['light.highlights', 'add', -30],
    ['light.shadows', 'add', 15],
    ['wb.temp', 'add', -6],
    ['color.vibrance', 'add', 18],
    ['hsl.blue.s', 'add', 15],
    ['hsl.blue.l', 'add', -6],
    ['hsl.aqua.s', 'add', 12],
    ['detail.sharpAmount', 'add', 35],
  ]),
  p('christmas', 'style', 'Christmas', 'Navidad', [
    ['wb.temp', 'add', 10],
    ['light.contrast', 'add', 14],
    ['light.highlights', 'add', -15],
    ['color.vibrance', 'add', 10],
    ['hsl.red.s', 'add', 18],
    ['hsl.green.s', 'add', 12],
    ['hsl.green.l', 'add', -5],
    ['effects.vignette.amount', 'add', -20],
    ['effects.splitTone.highlightHue', 'set', 45],
    ['effects.splitTone.highlightSat', 'add', 12],
  ]),
  p('copper', 'style', 'Copper & Warm Metal', 'Cobre y metal cálido', [
    ['wb.temp', 'add', 14],
    ['light.contrast', 'add', 16],
    ['light.whites', 'add', 10],
    ['light.highlights', 'add', -15],
    ['light.blacks', 'add', -8],
    ['hsl.orange.s', 'add', 15],
    ['hsl.orange.l', 'add', 5],
    ['hsl.red.s', 'add', 6],
    ['effects.splitTone.highlightHue', 'set', 30],
    ['effects.splitTone.highlightSat', 'add', 15],
    ['effects.splitTone.shadowHue', 'set', 200],
    ['effects.splitTone.shadowSat', 'add', 6],
    ['detail.sharpAmount', 'add', 50],
  ]),

  // ── Menu & delivery ──────────────────────────────────────────────────────────
  p('delivery-apps', 'menu', 'PedidosYa / Uber Eats', 'PedidosYa / Uber Eats', [
    ['light.exposure', 'add', 0.3],
    ['light.contrast', 'add', 22],
    ['light.highlights', 'add', -25],
    ['light.shadows', 'add', 15],
    ['light.blacks', 'add', -8],
    ['wb.temp', 'add', 4],
    ['color.vibrance', 'add', 30],
    ['color.saturation', 'add', 10],
    ['hsl.red.s', 'add', 10],
    ['hsl.orange.s', 'add', 8],
    ['cleanWhites.amount', 'add', 40],
    ['detail.sharpAmount', 'add', 80],
  ]),
  p('whatsapp', 'menu', 'WhatsApp Catalog', 'Catálogo de WhatsApp', [
    ['light.exposure', 'add', 0.5],
    ['light.contrast', 'add', -8],
    ['light.highlights', 'add', -20],
    ['light.shadows', 'add', 35],
    ['wb.temp', 'add', -6],
    ['color.vibrance', 'add', 15],
    ['cleanWhites.amount', 'add', 40],
    ['detail.nrLuma', 'add', 25],
    ['detail.nrColor', 'add', 30],
    ['detail.sharpAmount', 'add', 30],
  ]),
  p('facebook-ad', 'menu', 'Facebook Ad', 'Anuncio de Facebook', [
    ['light.exposure', 'add', 0.3],
    ['light.contrast', 'add', 15],
    ['light.highlights', 'add', -25],
    ['light.shadows', 'add', 10],
    ['wb.temp', 'add', 4],
    ['color.vibrance', 'add', 22],
    ['color.saturation', 'add', 10],
    ['hsl.red.s', 'add', 12],
    ['effects.vignette.amount', 'add', -25],
    ['detail.sharpAmount', 'add', 45],
  ]),
  p('tiktok-reels', 'menu', 'TikTok / Reels', 'TikTok / Reels', [
    ['light.exposure', 'add', 0.1],
    ['light.contrast', 'add', 18],
    ['light.blacks', 'add', -10],
    ['wb.temp', 'add', 4],
    ['color.saturation', 'add', 8],
    ['color.vibrance', 'add', 15],
    ['effects.splitTone.shadowHue', 'set', 190],
    ['effects.splitTone.shadowSat', 'add', 18],
    ['effects.splitTone.highlightHue', 'set', 35],
    ['effects.splitTone.highlightSat', 'add', 15],
    ['effects.vignette.amount', 'add', -10],
    ['detail.sharpAmount', 'add', 40],
  ]),
];
